import React, { useState } from "react";
import { TrendingUp, RefreshCw, X, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * ForecastButton — small inline button that on click opens a popover with
 * `/api/ai/demand-forecast/{sku}` output: narrative + suggested reorder qty.
 *
 * Props:
 *   sku: string
 *   onApply?: (suggested_qty: number) => void   // called when user clicks "Apply to qty"
 *   variant?: "button" | "chip"                 // visual style
 *   testId?: string
 */
export default function ForecastButton({ sku, onApply, variant = "button", testId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchForecast = async () => {
    if (!sku) { toast.error("No SKU to forecast"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/demand-forecast/${encodeURIComponent(sku)}`);
      setData(data);
      setOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const btnClass = variant === "chip"
    ? "inline-flex items-center gap-1 h-6 px-2 text-[10.5px] font-semibold text-indigo-700 hover:bg-indigo-50 border border-indigo-200 rounded-sm"
    : "inline-flex items-center gap-1 h-7 px-2.5 text-[11.5px] font-semibold text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 rounded-sm";

  return (
    <>
      <button
        onClick={fetchForecast}
        disabled={loading || !sku}
        data-testid={testId || `forecast-${sku}`}
        className={btnClass}
        title="AI-powered demand forecast"
      >
        {loading ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />}
        Forecast
      </button>

      {open && data && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40" onClick={() => setOpen(false)}>
          <div className="w-[520px] max-w-[92vw] bg-white border border-slate-200 rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="h-12 border-b border-slate-200 flex items-center justify-between px-4">
              <div className="flex items-center gap-2">
                <TrendingUp size={15} className="text-violet-600" />
                <div>
                  <div className="font-heading text-[13.5px] font-semibold text-slate-900">Demand Forecast</div>
                  <div className="text-[10.5px] font-mono text-slate-500">{data.system_sku}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-900"><X size={15} /></button>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                  <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Avg / day</div>
                  <div className="font-mono text-sm font-bold text-slate-900">{data.avg_daily_units ?? 0}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                  <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">30-day forecast</div>
                  <div className="font-mono text-sm font-bold text-indigo-800">{data.forecast_30d ?? 0}</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-2">
                  <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Trend</div>
                  <div className={`font-mono text-sm font-bold ${data.trend_ratio >= 1 ? "text-emerald-700" : "text-red-700"}`}>
                    {data.trend_ratio ?? 1}x
                  </div>
                </div>
              </div>
              <div className="rounded-sm border border-violet-200 bg-violet-50 p-3 mb-3">
                <div className="text-[10.5px] uppercase tracking-wider text-violet-800 font-bold mb-1">Suggested reorder qty</div>
                <div className="font-mono text-2xl font-bold text-violet-900">{data.suggested_reorder_qty ?? 0}</div>
                <div className="text-[11px] text-violet-800 mt-1">
                  Includes safety stock of {data.safety_stock ?? 0} units.
                  {data.rto_events_90d > 0 && <> {data.rto_events_90d} RTO event{data.rto_events_90d === 1 ? "" : "s"} in the last 90 days.</>}
                </div>
              </div>
              {(data.narrative || data.fallback) && (
                <div className="text-[12.5px] text-slate-700 leading-relaxed">{data.narrative || data.fallback}</div>
              )}
              <div className="text-[10.5px] text-slate-500 font-mono mt-3">
                Based on {data.history_days ?? 0} days of history
                {data.stddev_daily_units != null && <> · σ = {data.stddev_daily_units}</>}
              </div>
            </div>
            <div className="h-12 border-t border-slate-200 flex items-center justify-end gap-2 px-4">
              <button onClick={() => setOpen(false)} className="h-8 px-3 text-[12.5px] text-slate-700 hover:bg-slate-50 border border-slate-200 rounded-sm">Close</button>
              {onApply && data.suggested_reorder_qty > 0 && (
                <button
                  onClick={() => { onApply(data.suggested_reorder_qty); setOpen(false); toast.success(`Applied ${data.suggested_reorder_qty} units`); }}
                  data-testid="forecast-apply-btn"
                  className="h-8 px-3 text-[12.5px] font-semibold text-white bg-violet-600 hover:bg-violet-700 rounded-sm"
                >
                  Apply {data.suggested_reorder_qty} to Qty
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
